import { PaginationState, Table } from "@tanstack/react-table";
import React, { Dispatch, SetStateAction, useEffect } from "react";
import { CustomTableProps } from "@/lib/types/table.types";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FormSelect } from "@/components/FormComponents/FormSelect";

interface PaginationProps<T extends Record<string, any>> {
  table?: Table<T>;
  rows: CustomTableProps<T>["rows"];
  pagination: PaginationState;
  setPagination: Dispatch<SetStateAction<PaginationState>>;
  manualPagination?: boolean;
  loading?: boolean;
}

const Pagination = <T extends Record<string, any>>({
  table,
  setPagination,
  pagination,
  rows,
  manualPagination,
  loading,
}: PaginationProps<T>) => {
  const pageSize = table
    ? table.getState().pagination.pageSize
    : pagination.pageSize;
  const currentPage = table
    ? table.getState().pagination.pageIndex + 1
    : pagination.pageIndex + 1;
  const totalPages = table
    ? table.getPageCount()
    : Math.ceil((rows?.rowCount ?? 0) / pageSize);
  const renderPageNumbers = () => {
    const pageNumbers = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i += 1) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);

      if (currentPage <= 4) {
        for (let i = 2; i <= 5; i += 1) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pageNumbers.push("...");
        for (let i = totalPages - 4; i <= totalPages; i += 1) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i += 1) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  const generatePageSizes = (maxSize: number) => {
    const sizes: number[] = [];
    let current = 10;

    while (current <= maxSize) {
      if (current !== maxSize) {
        sizes.push(current);
      }

      if (current < 100) {
        current += 10;
      } else if (current < 1000) {
        current += 100;
      } else {
        current += 1000;
      }
    }
    return sizes;
  };

  const pageSizeOptions = generatePageSizes(rows.rowCount ?? 0).map((size) => ({
    value: size.toString(),
    label: size.toString(),
  }));
  if (!manualPagination || (rows.rowCount ?? 0) <= 1000) {
    pageSizeOptions.push({
      value: rows.rowCount?.toString() ?? "0",
      label: `Hepsini Göster (${rows.rowCount})`,
    });
  }

  useEffect(() => {
    if (manualPagination && (rows.rowCount ?? 0) > 1000) {
      setPagination({ ...pagination, pageSize: 1000 });
    }
  }, [manualPagination, rows.rowCount, pagination, setPagination]);

  useEffect(() => {
    if (
      manualPagination &&
      !loading &&
      pagination &&
      pagination?.pageIndex > totalPages - 1
    ) {
      setPagination({ ...pagination, pageIndex: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualPagination, rows.pageCount, setPagination, totalPages, loading]);

  const getPageNumber = () => {
    if (manualPagination) {
      if (totalPages === 1) {
        return pageSizeOptions[pageSizeOptions.length - 1].value.toString();
      }
      const pageSizeOption = pageSizeOptions.find(
        (option) => option.value === pagination.pageSize.toString()
      );
      if (!pageSizeOption) {
        return pageSizeOptions[0].value.toString();
      }
      if (pagination.pageSize >= (rows?.rowCount ?? 0)) {
        return pagination.pageSize.toString();
      }
      return pagination.pageSize.toString();
    }
    if (pageSize >= rows.data.length) {
      return rows.data.length.toString();
    }
    return pageSize.toString();
  };

  return (
    <div className="flex items-center gap-2 select-none mt-auto sticky z-20 bottom-2 shadow border-t bg-gray-200 p-5 rounded-2xl">
      <Button
        size="icon"
        className="text-gray-800"
        variant="ghost"
        onClick={() =>
          table
            ? table.previousPage()
            : setPagination!((currentValue) => ({
                ...currentValue,
                pageIndex: currentValue.pageIndex - 1,
              }))
        }
        disabled={
          table ? !table.getCanPreviousPage() : pagination!.pageIndex === 0
        }
      >
        <ChevronLeft />
      </Button>

      <div className="flex items-center gap-1">
        {renderPageNumbers().map((pageNum) => (
          <React.Fragment key={pageNum}>
            {pageNum === "..." ? (
              <span className="px-2 h-8 w-8 flex items-center justify-center text-gray-800">
                ...
              </span>
            ) : (
              <div>
                <Button
                  variant={currentPage === pageNum ? "default" : "ghost"}
                  className="min-w-[32px] h-8 flex items-center justify-center px-1"
                  onClick={() => {
                    const newPage = (pageNum as number) - 1;
                    if (table) {
                      table.setPageIndex(newPage);
                    } else {
                      setPagination!((prev) => ({
                        ...prev,
                        pageIndex: newPage,
                      }));
                    }
                  }}
                >
                  {pageNum}
                </Button>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <Button
        size="icon"
        className="text-gray-800"
        variant="ghost"
        onClick={() =>
          table
            ? table.nextPage()
            : setPagination!((currentValue) => ({
                ...currentValue,
                pageIndex: currentValue.pageIndex + 1,
              }))
        }
        disabled={
          table
            ? !table.getCanNextPage()
            : pagination!.pageIndex === totalPages - 1
        }
      >
        <ChevronRight />
      </Button>

      <FormSelect
        onChange={(e) => {
          if (table) {
            table.setPageSize(Number(e.target.value));
          } else {
            setPagination!({
              ...pagination!,
              pageSize: Number(e.target.value),
            });
          }
        }}
        value={getPageNumber()}
        options={pageSizeOptions}
      />
    </div>
  );
};

export { Pagination };
